import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css } from 'lit';
import { nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { html } from 'lit/static-html.js';

import { groupTraitKey } from '../../../core/group-by/trait.js';
import type { SingleView } from '../../../core/index.js';
import { isKanbanL1Groupable } from '../groupable.js';
import { group2TraitKey } from '../kanban-view-manager.js';
import { isL2Groupable } from '../l2-matcher.js';

const styles = css`
  affine-data-view-kanban-header {
    display: flex;
    justify-content: space-between;
    padding: 4px;
    gap: 8px;
  }

  .select-group {
    border-radius: 8px;
    padding: 4px 8px;
    cursor: pointer;
    font-size: var(--data-view-cell-text-size);
  }

  .select-group:hover {
    background-color: var(--streamlain-hover-color);
  }

  .group-level-pair {
    display: flex;
    gap: 4px;
    align-items: center;
  }
`;

export class KanbanHeader extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = styles;

  private readonly clickGroup = (e: MouseEvent) => {
    const groupTrait = this.view.traitGet(groupTraitKey);
    if (!groupTrait) return;
    const group2 = this.view.traitGet(group2TraitKey);
    const dataSource = this.view.manager.dataSource;
    popMenu(popupTargetFromElement(e.target as HTMLElement), {
      options: {
        items: this.view
          .getBoundedPropertiesWindow()
          .properties.filter(
            column => column.id !== groupTrait.property$.value?.id
          )
          .filter(column => column.id !== group2?.property$.value?.id)
          .filter(column => isKanbanL1Groupable(dataSource, column.id))
          .map(column => {
            return menu.action({
              name: column.name$.value,
              select: () => {
                groupTrait.changeGroup(column.id);
              },
            });
          }),
      },
    });
  };

  private readonly clickGroup2 = (e: MouseEvent) => {
    const group2 = this.view.traitGet(group2TraitKey);
    const groupTrait = this.view.traitGet(groupTraitKey);
    if (!group2 || !groupTrait) return;
    const dataSource = this.view.manager.dataSource;
    popMenu(popupTargetFromElement(e.target as HTMLElement), {
      options: {
        items: this.view
          .getBoundedPropertiesWindow()
          .properties.filter(column => column.id !== group2.property$.value?.id)
          .filter(column => column.id !== groupTrait.property$.value?.id)
          // Single-cardinality whitelist: a card lives in exactly one
          // row. Multi-valued columns would clone the card across N
          // rows — that's the "one card, many lanes" antipattern the
          // 2-D grid is designed to avoid.
          .filter(column => isL2Groupable(dataSource, column.id))
          .map(column => {
            return menu.action({
              name: column.name$.value,
              select: () => {
                group2.changeGroup(column.id);
              },
            });
          }),
      },
    });
  };

  override render() {
    const group2 = this.view.traitGet(group2TraitKey);
    const group2Label = group2?.property$.value?.name$.value ?? 'None';
    const groupTrait = this.view.traitGet(groupTraitKey);
    const groupLabel = groupTrait?.property$.value?.name$.value ?? 'Column';
    const l1IsSet = !!groupTrait?.property$.value;
    return html`
      <div></div>
      <div class="group-level-pair">
        ${l1IsSet
          ? html`<div class="select-group" @click="${this.clickGroup2}">
              Row: ${group2Label}
            </div>`
          : nothing}
        <div class="select-group" @click="${this.clickGroup}">
          Column: ${groupLabel}
        </div>
      </div>
    `;
  }

  @property({ attribute: false })
  accessor view!: SingleView;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-data-view-kanban-header': KanbanHeader;
  }
}
